import { describeMention } from './useMentionSources';
import { scrubTextForModel } from '../../services/ai/modelBoundary';
import { CHAT_INPUT_MAX_HEIGHT } from './aiChatConstants';

/** Compact token count: 940 → "940", 12_300 → "12.3K", 1_250_000 → "1.25M". */
export function formatTokenCount(n) {
  const num = Number(n) || 0;
  if (num < 1000) return String(num);
  if (num < 1_000_000) {
    const k = num / 1000;
    return `${k < 10 ? k.toFixed(1) : Math.round(k)}K`;
  }
  const m = num / 1_000_000;
  return `${m < 10 ? m.toFixed(2) : m.toFixed(1)}M`;
}

export const SLASH_COMMANDS = [
  { type: 'command', id: 'clear', label: 'clear', meta: 'Clear the chat history' },
];

export function filterCommands(query) {
  const q = (query || '').toLowerCase();
  return SLASH_COMMANDS.filter((c) => !q || c.label.startsWith(q));
}

export function buildReferenceOptions(collections, environments) {
  const items = [];
  for (const col of collections || []) {
    items.push({
      type: 'collection',
      id: String(col.id),
      label: col.name,
      meta: 'Collection',
      data: col,
    });
    for (const req of col.requests || []) {
      items.push({
        type: 'request',
        id: String(req.id),
        label: req.name || req.url || 'Untitled request',
        meta: `${req.method || 'GET'} · ${col.name}`,
        data: { ...req, collection_id: col.id, collection_name: col.name },
      });
    }
  }
  for (const env of environments || []) {
    items.push({
      type: 'environment',
      id: String(env.id),
      label: env.name,
      meta: 'Environment',
      data: env,
    });
  }
  return items;
}

export function filterReferenceOptions(options, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return options.slice(0, 40);
  return options
    .filter((o) => o.label.toLowerCase().includes(q) || o.meta.toLowerCase().includes(q))
    .slice(0, 40);
}

export function buildReferenceContext(refs) {
  if (!refs?.length) return '';
  const lines = refs.map((r) => {
    if (r.type === 'request') {
      const d = r.data || {};
      return `- Request "${r.label}" (id: ${r.id}, ${d.method || 'GET'} ${scrubTextForModel(d.url || '')}, collection: ${d.collection_name || d.collection_id || 'unknown'})`;
    }
    if (r.type === 'collection') {
      const count = (r.data?.requests || []).length;
      return `- Collection "${r.label}" (id: ${r.id}, ${count} request${count === 1 ? '' : 's'})`;
    }
    if (r.type === 'environment') {
      return `- Environment "${r.label}" (id: ${r.id})`;
    }
    // Documents / boards / DB connections — each line names the TOOL that
    // reads it, so the model follows the reference instead of guessing.
    const extra = describeMention(r);
    if (extra) return extra;
    return `- ${r.label}`;
  });
  return `\n\n[Referenced context:\n${lines.join('\n')}\n]`;
}

export function refKey(ref) {
  return `${ref.type}:${ref.id}`;
}

export function adjustChatInputHeight(textarea) {
  if (!textarea) return;
  textarea.style.height = '0px';
  const next = Math.min(textarea.scrollHeight, CHAT_INPUT_MAX_HEIGHT);
  textarea.style.height = `${next}px`;
  const atMax = textarea.scrollHeight > CHAT_INPUT_MAX_HEIGHT;
  textarea.style.overflowY = atMax ? 'auto' : 'hidden';
  textarea.classList.toggle('scrollbar-none', atMax);
}
